import * as React from "react";
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { useI18n } from "@/i18n/I18nProvider";
import { getStatusTone, STATUS_LABEL_KEY } from "@/lib/statusTokens";

// P1 (ADR-P1-001) — Gotcha Tailwind: el `tone` NUNCA se interpola en className en
// runtime (`bg-${tone}/10` se purga). Cada tone es una entrada cva con clases
// 100% literales, escritas a mano, para que el purge de Tailwind las conserve.
const toneVariants = cva(
  "inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold",
  {
    variants: {
      tone: {
        critical: "bg-critical/10 text-critical border-critical/20",
        high: "bg-high/10 text-high border-high/20",
        medium: "bg-medium/10 text-medium border-medium/20",
        low: "bg-low/10 text-low border-low/20",
        info: "bg-info/10 text-info border-info/20",
        neutral: "bg-muted text-muted-foreground border-border",
        brand: "bg-primary/10 text-primary border-primary/20",
      },
    },
    defaultVariants: { tone: "neutral" },
  }
);

// Punto sólido (celdas densas de tabla/heatmap) — clases literales, un tone = una entrada.
const DOT = {
  critical: "bg-critical",
  high: "bg-high",
  medium: "bg-medium",
  low: "bg-low",
  info: "bg-info",
  neutral: "bg-neutral",
  brand: "bg-primary",
};

// Texto-solo (sin chrome de badge) — para sitios que hoy pintan solo un ícono/texto
// (AuditChecklistTab, ImplementationWizard, Dashboard/Reports score bands). Mismas
// clases literales que toneVariants, expuestas para reuso fuera del badge completo.
export const TEXT_TONE = {
  critical: "text-critical",
  high: "text-high",
  medium: "text-medium",
  low: "text-low",
  info: "text-info",
  neutral: "text-muted-foreground",
  brand: "text-primary",
};

// Fondo suave "bg-{tone}/10" sin borde — para chips/tiles (p.ej. contadores por
// nivel en RiskRegister) que necesitan el mismo tinte que el badge pero sin su
// chrome. Clases literales, igual que toneVariants — evita reinterpolar en cada página.
export const BG_TONE_10 = {
  critical: "bg-critical/10",
  high: "bg-high/10",
  medium: "bg-medium/10",
  low: "bg-low/10",
  info: "bg-info/10",
  neutral: "bg-muted",
  brand: "bg-primary/10",
};

function StatusBadge({ kind, value, className, ...props }) {
  const { t } = useI18n();
  const tone = getStatusTone(kind, value);
  const labelKey = STATUS_LABEL_KEY[kind]?.[value];
  const label = labelKey ? t(labelKey) : value;
  return (
    <div className={cn(toneVariants({ tone }), className)} {...props}>
      {label}
    </div>
  );
}

function StatusDot({ kind, value, className, ...props }) {
  const tone = getStatusTone(kind, value);
  return (
    <span
      className={cn("inline-block h-2.5 w-2.5 rounded-full", DOT[tone], className)}
      {...props}
    />
  );
}

export { StatusBadge, StatusDot, toneVariants, DOT };
